import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Plane } from 'lucide-react';
import { FaFacebookF, FaInstagramSquare, FaLinkedin, FaYoutube } from 'react-icons/fa';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-gradient-to-br from-red-600 via-red-500 to-red-600 text-white overflow-hidden">
      {/* Innovative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-700/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
      </div>
      
      {/* Decorative Lines */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
      <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
        <Plane className="w-6 h-6 text-white/20 rotate-90" />
      </div>
      
      <div className="container mx-auto px-4 pt-16 pb-8 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Brand Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              <h2 className="text-3xl font-bold">Skyford Aviation</h2>
              <p className="text-xl font-light italic">Learn. Do. Live</p>
            </div>
            <p className=" max-w-sm">
              Empowering the next generation of aviation professionals with world-class training and education.
            </p>
            <div className="flex gap-6">
              <a href="https://www.facebook.com/skyfordaviation/" className="hover:scale-110 transition-transform hover:text-white/90" aria-label="Facebook">
                <FaFacebookF className="w-6 h-6" />
              </a>
              <a href="https://www.instagram.com/skyford_aviation?igsh=N2phd2lrbHN3N2E5" className="hover:scale-110 transition-transform hover:text-white/90" aria-label="Instagram">
                <FaInstagramSquare className="w-6 h-6" />
              </a>
              <a href="https://www.linkedin.com/company/skyford-aviation/" className="hover:scale-110 transition-transform hover:text-white/90" aria-label="Instagram">
                <FaLinkedin className="w-6 h-6" />
              </a>
              <a href="http://www.youtube.com/@skyfordaviation" className="hover:scale-110 transition-transform hover:text-white/90" aria-label="YouTube">
                <FaYoutube className="w-7 h-7" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="sm:ml-8">
            <h3 className="text-xl font-semibold mb-6">Quick Links</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ul className="space-y-4">
                {[
                  { to: "/", label: "Home" },
                  { to: "/courses", label: "Courses" },
                  { to: "/placements", label: "Placements" },
                  { to: "/media", label: "Media" },
                  { to: "/about", label: "About" },
                  { to: "/contact", label: "Contact" }
                ].map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.to} 
                      className="group relative flex items-center gap-3 w-fit"
                    >
                      <span className="relative z-10 transition-transform group-hover:translate-x-2">
                        {link.label}
                      </span>
                      <Plane 
                        className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform -rotate-12 group-hover:translate-x-1"
                      />
                      <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-white transition-all group-hover:w-full"></span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-red-400/10 rounded-full blur-2xl"></div>
            <h3 className="text-xl font-semibold mb-6">Get in Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 hover:translate-x-2 transition-all cursor-pointer group">
                <Mail className="w-5 h-5 text-white/70 flex-shrink-0 group-hover:text-white" />
                <a href="mailto:admin@skyfordaviation.in" className="hover:underline">
                  admin@skyfordaviation.in
                </a>
              </li>
              <li className="flex items-center gap-3 hover:translate-x-2 transition-all cursor-pointer group">
                <Phone className="w-5 h-5 text-white/70 flex-shrink-0 group-hover:text-white" />
                <a href="tel:+919895332211" className="hover:underline">
                  +91 9895332211
                </a>
              </li>
              <li className="flex items-start gap-3 ">
                <MapPin className="w-5 h-5 text-white/70 flex-shrink-0 " />
                <p>Calicut | Cochin | Thrissur | Tirur | Perinthalmanna</p>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="text-center">
            <p className="text-white/70 text-sm">
              © {currentYear} Skyford Aviation. All rights reserved. Designed by <a href="https://www.elzonex.com/" className="hover:underline text-white">ElzoneX </a>Calicut.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;